import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import moment from 'moment';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { filter, map, switchMap, takeUntil } from 'rxjs/operators';

import {
  Feature,
  FeatureNames,
  HolidayProgramCommands,
  HolidayProgramProperties,
} from '@vi-iot/client';

import { InstallationDetailsStore, InstallationFeaturesStore, isReady, Platform } from '@store';

@Component({
  selector: 'app-holiday-mode-panel',
  templateUrl: './holiday-mode-panel.component.html',
  styleUrls: ['./holiday-mode-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HolidayModePanelComponent implements OnInit, OnDestroy {
  departureDate: moment.Moment;
  returnDate: moment.Moment;
  isScheduled: boolean;
  isOutdated: boolean;
  platform: Platform;

  holiday$: Observable<Feature<HolidayProgramProperties, HolidayProgramCommands>>;
  circuitIdToDisplay: number;

  ngUnsubscribe: Subject<void> = new Subject<void>();

  @Input() circuitId: string;
  @Input() isMultiFamily = false;
  @Input() isHolidayAtHome = false;

  constructor(
    private installationDetailsStore: InstallationDetailsStore,
    private installationFeaturesStore: InstallationFeaturesStore
  ) {}

  get deactivateDialogTitle(): string {
    return this.isHolidayAtHome
      ? '+INSTALLATIONS.INSTALLATION.HOLIDAY_AT_HOME.DEACTIVATE_DIALOG.TITLE'
      : '+INSTALLATIONS.INSTALLATION.HOLIDAY_MODE.DEACTIVATE_DIALOG.TITLE';
  }

  get deactivateDialogContent(): string {
    return this.isHolidayAtHome
      ? '+INSTALLATIONS.INSTALLATION.HOLIDAY_AT_HOME.DEACTIVATE_DIALOG.DESCRIPTION'
      : '+INSTALLATIONS.INSTALLATION.HOLIDAY_MODE.DEACTIVATE_DIALOG.DESCRIPTION';
  }

  ngOnInit(): void {
    if (this.isMultiFamily) {
      this.holiday$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
        this.isHolidayAtHome
          ? FeatureNames.circuitHolidayAtHome(this.circuitId)
          : FeatureNames.circuitHolidayProgram(this.circuitId)
      );
    } else {
      this.holiday$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
        this.isHolidayAtHome
          ? FeatureNames.circuitSingleFamilyHolidayAtHome
          : FeatureNames.circuitSingleFamilyHolidayProgram
      );
    }

    this.initHolidayModeSubscription();
    this.circuitIdToDisplay = Number(this.circuitId) + 1;
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  initHolidayModeSubscription(): void {
    this.holiday$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        filter((feature) => isReady(feature)),
        map((feature) => ({
          start: feature?.properties?.start?.value || '',
          end: feature?.properties?.end?.value || '',
          active: feature?.properties?.active?.value || false,
        })),
        switchMap((value) =>
          combineLatest([of(value), this.installationDetailsStore.getMainDevicePlatform()])
        ),
        filter((fork) => !!fork[1])
      )
      .subscribe((fork) => {
        const value = fork[0];
        const platform = fork[1];
        this.departureDate =
          platform === Platform.E3
            ? moment(value.start).startOf('day')
            : moment(value.start).add(1, 'day').startOf('day');

        this.returnDate =
          platform === Platform.E3
            ? moment(value.end).endOf('day')
            : moment(value.end).subtract(1, 'day').endOf('day');
        this.isScheduled = this.departureDate.isValid() && this.returnDate.isValid();
        this.isOutdated = moment(new Date()).isAfter(this.returnDate, 'day');
      });
  }

  onUncheduleHolidayMode(): void {
    let featureName: string;

    if (this.isMultiFamily) {
      featureName = this.isHolidayAtHome
        ? FeatureNames.circuitHolidayAtHome(this.circuitId)
        : FeatureNames.circuitHolidayProgram(this.circuitId);
    } else {
      featureName = this.isHolidayAtHome
        ? FeatureNames.circuitSingleFamilyHolidayAtHome
        : FeatureNames.circuitSingleFamilyHolidayProgram;
    }

    this.installationFeaturesStore.updateFeature(
      featureName,
      [false, '', ''],
      ['active', 'start', 'end'],
      null,
      'unschedule'
    );
  }
}
